import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Pagination Components
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showPrevNext = true,
  maxVisiblePages = 5,
}) => {
  const generatePageNumbers = () => {
    if (totalPages <= maxVisiblePages) {
      // Case 1: Show all pages if total pages <= maxVisiblePages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      // Case 2: Near the beginning - show first 5 pages + ... + last page
      return [...Array.from({ length: maxVisiblePages }, (_, i) => i + 1), '...', totalPages];
    }

    if (currentPage >= totalPages - 2) {
      // Case 3: Near the end - show first page + ... + last 5 pages
      const startPage = totalPages - maxVisiblePages + 1;
      return [1, '...', ...Array.from({ length: maxVisiblePages }, (_, i) => startPage + i)];
    }

    // Case 4: In the middle - show first + ... + surrounding pages + ... + last
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const pageNumbers = generatePageNumbers();

  return (
    <nav className="flex items-center justify-center space-x-2">
      {showPrevNext && (
        <PaginationArrow
          direction="prev"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        />
      )}

      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            <PaginationEllipsis key={`ellipsis-${index}`} />
          );
        }
        return (
          <PaginationItem
            key={page}
            page={page}
            isSelected={page === currentPage}
            onClick={() => onPageChange(page)}
          />
        );
      })}

      {showPrevNext && (
        <PaginationArrow
          direction="next"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        />
      )}
    </nav>
  );
};

const PaginationItem = ({ page, isSelected, onClick }) => {
  const getStateClasses = () => {
    if (isSelected) {
      return "bg-secondary-2 border-secondary-2 text-white";
    }
    return `
      bg-white border-[#E5E5E5] text-[#333] hover:bg-[#F5F1EA] hover:border-secondary-2 hover:text-secondary-2
    `;
  };

  return (
    <button
      onClick={onClick}
      className={`
        h-8 w-8 rounded-lg border text-sm font-normal transition-colors focus-visible:ring-misc-focus focus-visible:ring-[2px] focus-visible:ring-offset-1 
        ${getStateClasses()}
      `}
    >
      {page}
    </button>
  );
};

const PaginationEllipsis = () => {
  return (
    <span className="h-8 w-8 rounded flex items-center justify-center text-sm text-[#666] focus-visible:ring-misc-focus focus-visible:ring-[2px] focus-visible:ring-offset-1">
      ...
    </span>
  );
};

const PaginationArrow = ({ direction, onClick, disabled }) => {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        h-8 w-8 rounded flex items-center justify-center transition-colors 
        ${
          disabled
            ? "text-[#999] cursor-not-allowed"
            : "text-[#333] hover:bg-[#F5F1EA] hover:text-secondary-2 focus:text-secondary-2"
        }
      `}
    >
      <Icon className="h-8 w-8" />
    </button>
  );
};

export { Pagination };
